// Комментарий на русском: iOS Safari/keyboard/viewport фиксы вынесены из страницы,
// чтобы `MessagesPageClient.tsx` не раздувался и этот кусок можно было править отдельно.

type ScrollRef = { current: HTMLDivElement | null };
type BoolRef = { current: boolean };

export function installMessengerViewportFix(opts: {
  messagesScrollRef: ScrollRef;
  composeInChatRef: BoolRef;
  // Комментарий на русском: новый скролл — прокрутка должна идти через виртуализатор, а не через scrollTop.
  scrollToBottom?: () => void;
}) {
  let raf = 0;
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  let prevKbOpen = false;

  const apply = () => {
    // Комментарий на русском: высоту приложения на iOS считаем по visualViewport (так клавиатура не перекрывает UI).
    const layoutH = Math.max(0, Math.round(window.innerHeight || 0));
    const visualH = Math.max(0, Math.round(vv?.height || layoutH));
    const h = visualH;
    const top = Math.max(0, Math.round(vv?.offsetTop || 0));
    // Комментарий на русском: высота клавиатуры (клавиатура + системные панели ввода).
    const kb = Math.max(0, layoutH - visualH);
    document.documentElement.style.setProperty('--app-height', `${h}px`);
    document.documentElement.style.setProperty('--app-top', `${top}px`);
    document.documentElement.style.setProperty('--kb', `${kb}px`);

    // Комментарий на русском: отступ плавающих кнопок — без клавиатуры ~10px, с клавиатурой ~3px.
    const keyboardOpen = kb > 120;
    document.documentElement.style.setProperty('--compose-btn-bottom', keyboardOpen ? '3px' : '10px');

    // Комментарий на русском:
    // На iOS visualViewport может дергаться очень часто (scroll/resize при клавиатуре),
    // поэтому скроллим вниз ТОЛЬКО когда клавиатура реально открыта и включён composeInChat.
    if (opts.composeInChatRef.current && keyboardOpen && !prevKbOpen) {
      prevKbOpen = true;
      if (opts.scrollToBottom) {
        opts.scrollToBottom();
      } else {
        // Fallback: если virtuoso ещё не доступен.
        const scroller = opts.messagesScrollRef.current;
        if (scroller) scroller.scrollTop = scroller.scrollHeight;
      }
    }
    if (!keyboardOpen) prevKbOpen = false;
  };

  const schedule = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(apply);
  };

  schedule();

  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  window.addEventListener('focusin', schedule);
  window.addEventListener('focusout', schedule);
  vv?.addEventListener('resize', schedule);
  vv?.addEventListener('scroll', schedule);

  // iOS: Safari может прокручивать document даже при overflow:hidden (особенно при фокусе textarea).
  // Поэтому на iOS делаем "жёсткий" scroll-lock через position:fixed у body и фильтруем touchmove.
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iP(ad|hone|od)/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
  const scrollY = window.scrollY || 0;

  const prevHtmlOverflow = document.documentElement.style.overflow;
  const prevHtmlHeight = document.documentElement.style.height;
  const prevHtmlOverscroll = (document.documentElement.style as any).overscrollBehaviorY || '';
  const prevBodyOverflow = document.body.style.overflow;
  const prevBodyHeight = document.body.style.height;
  const prevBodyOverscroll = (document.body.style as any).overscrollBehaviorY || '';
  const prevBodyPosition = document.body.style.position;
  const prevBodyTop = document.body.style.top;
  const prevBodyLeft = document.body.style.left;
  const prevBodyRight = document.body.style.right;
  const prevBodyWidth = document.body.style.width;

  document.documentElement.style.overflow = 'hidden';
  document.documentElement.style.height = 'var(--app-height, 100vh)';
  (document.documentElement.style as any).overscrollBehaviorY = 'none';
  document.body.style.overflow = 'hidden';
  document.body.style.height = 'var(--app-height, 100vh)';
  (document.body.style as any).overscrollBehaviorY = 'none';

  let activeScroller: HTMLElement | null = null;

  const findScroller = (t: EventTarget | null) =>
    t instanceof Element ? (t.closest('[data-scroll-lock-allow]') as HTMLElement | null) : null;

  const onTouchStart = (e: TouchEvent) => {
    activeScroller = findScroller(e.target);
  };

  const onTouchMove = (e: TouchEvent) => {
    // Комментарий на русском: раньше мы блокировали touchmove на “границах” (top/bottom),
    // из‑за чего на iOS появлялся эффект “залипания”: после достижения конца списка повторный свайп
    // в ту же сторону не срабатывал, пока не сделаешь свайп обратно.
    //
    // Сейчас делаем проще и стабильнее:
    // - если палец внутри зоны, помеченной `data-scroll-lock-allow` — НЕ блокируем (пусть iOS сама скроллит/резинит),
    // - если палец вне разрешённых зон — блокируем, чтобы фон/страница не скроллились.
    const scroller = activeScroller || findScroller(e.target);
    if (!scroller) {
      if (e.cancelable) e.preventDefault();
      return;
    }
    activeScroller = scroller;
  };

  // Комментарий на русском:
  // На iOS иногда “проскакивает” window-scroll даже при overflow:hidden.
  // Важно: НЕ мешаем скроллу внутри разрешённых зон (`data-scroll-lock-allow`), иначе будут рывки/“телепорты”.
  let scrollLockRaf = 0;
  const onScroll = () => {
    if (!isIOS) return;
    // Если палец в разрешённой зоне — не трогаем window-scroll (иначе ломаем инерцию).
    if (activeScroller) return;
    if ((window.scrollY || 0) === 0) return;
    cancelAnimationFrame(scrollLockRaf);
    scrollLockRaf = requestAnimationFrame(() => {
      try {
        window.scrollTo(0, 0);
      } catch {}
    });
  };

  if (isIOS) {
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  return () => {
    document.documentElement.style.overflow = prevHtmlOverflow;
    document.documentElement.style.height = prevHtmlHeight;
    (document.documentElement.style as any).overscrollBehaviorY = prevHtmlOverscroll;
    document.body.style.overflow = prevBodyOverflow;
    document.body.style.height = prevBodyHeight;
    (document.body.style as any).overscrollBehaviorY = prevBodyOverscroll;
    document.body.style.position = prevBodyPosition;
    document.body.style.top = prevBodyTop;
    document.body.style.left = prevBodyLeft;
    document.body.style.right = prevBodyRight;
    document.body.style.width = prevBodyWidth;
    document.documentElement.style.removeProperty('--app-height');
    document.documentElement.style.removeProperty('--app-top');
    document.documentElement.style.removeProperty('--kb');
    document.documentElement.style.removeProperty('--compose-btn-bottom');
    document.removeEventListener('touchstart', onTouchStart as any);
    document.removeEventListener('touchmove', onTouchMove as any);
    window.removeEventListener('scroll', onScroll as any);
    if (isIOS) window.scrollTo(0, scrollY);
    cancelAnimationFrame(scrollLockRaf);
    window.removeEventListener('resize', schedule);
    window.removeEventListener('orientationchange', schedule);
    window.removeEventListener('focusin', schedule);
    window.removeEventListener('focusout', schedule);
    vv?.removeEventListener('resize', schedule);
    vv?.removeEventListener('scroll', schedule);
    cancelAnimationFrame(raf);
  };
}


